import { useEffect, useState } from 'react';
import type { MonthlyFmtTask } from '@cortex/ado-task-fmt';
import { ManualView } from './ManualView';
import { hasManual } from '@/tasks/sanitize-html';

/**
 * 入力候補。既にある値から作り、選んでも手で書いても良いようにする。
 */
export interface EditorSuggestions {
  /** 事業の候補 */
  businesses: readonly string[];
  /** 大分類の候補 */
  categories: readonly string[];
  /** 中分類の候補 */
  middleCategories: readonly string[];
  /** 予定営業日の候補 */
  dueRules: readonly string[];
  /** 担当者の候補 */
  assignees: readonly string[];
}

/**
 * MonthlyFmtTaskEditor の props。
 */
export interface MonthlyFmtTaskEditorProps {
  /** 編集するタスク。新規追加のときは null */
  task: MonthlyFmtTask | null;
  /**
   * 新規追加のときに、はじめから入れておく値。
   *
   * 大分類の見出しから足すときに、その大分類を埋めておくために使う。編集のときは効かない
   */
  defaults?: Partial<MonthlyFmtTask>;
  /** 入力候補 */
  suggestions: EditorSuggestions;
  /** 保存するときに呼ばれる */
  onSave: (task: MonthlyFmtTask) => void;
  /** 削除するときに呼ばれる。新規追加のときは渡らない */
  onDelete?: (id: string) => void;
  /** 閉じるときに呼ばれる */
  onClose: () => void;
}

/** 空のタスク。新規追加の初期値 */
/** @graph-connects none */
const EMPTY: MonthlyFmtTask = {
  id: '',
  business: '共通',
  majorCategory: '',
  middleCategory: '',
  title: '',
  dueRule: '',
  estimatedHours: 0.25,
  assignee: 'nako',
  manual: '',
};

/** @graph-connects none */
const FIELD_CLASS =
  'w-full rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-2.5 py-1.5 text-sm text-[var(--color-text-primary)]';

/** @graph-connects none */
const LABEL_CLASS = 'mb-1 block text-xs text-[var(--color-text-muted)]';

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 月次経理タスクの追加・編集・削除を画面中央のポップアップで行う。既にある値を入力候補に出し、マニュアルは書きながら出来上がりを見られる
 * @graph-connects ManualView [calls] マニュアルの出来上がりを描く
 * @graph-connects hasManual [calls] マニュアルが書かれているかを調べる
 */
export function MonthlyFmtTaskEditor({
  task,
  defaults = {},
  suggestions,
  onSave,
  onDelete,
  onClose,
}: MonthlyFmtTaskEditorProps) {
  const [draft, setDraft] = useState<MonthlyFmtTask>(task ?? { ...EMPTY, ...defaults });
  // 削除は 2 段にする。1 回のクリックで消えると取り返しがつかない
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // マニュアルを書いているか、出来上がりを見ているか
  const [previewingManual, setPreviewingManual] = useState(false);
  // 想定時間は空欄（未定）を許すので、数値ではなく入力中の文字列として持つ
  const [hours, setHours] = useState(
    task === null || task.estimatedHours === null ? '' : String(task.estimatedHours),
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const update = (patch: Partial<MonthlyFmtTask>) => {
    setDraft({ ...draft, ...patch });
  };

  // タスク名と大分類が無いと一覧に置き場所がないため、この 2 つだけ必須にする
  const canSave = draft.title.trim() !== '' && draft.majorCategory.trim() !== '';

  const textField = (
    label: string,
    value: string,
    listId: string | undefined,
    onChange: (next: string) => void,
  ) => (
    <label className="block">
      <span className={LABEL_CLASS}>{label}</span>
      <input
        type="text"
        value={value}
        list={listId}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className={FIELD_CLASS}
      />
    </label>
  );

  return (
    <>
      <button
        type="button"
        aria-label="編集を閉じる"
        onClick={onClose}
        className="fixed inset-0 z-10 cursor-default bg-[var(--color-text-primary)]/20"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={task === null ? 'タスクの追加' : 'タスクの編集'}
        className="fixed top-1/2 left-1/2 z-20 flex max-h-[85vh] w-[min(36rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-y-auto rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-6 py-5 shadow-[0_8px_32px_var(--color-shadow)]"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="m-0 text-base font-semibold text-[var(--color-text-primary)]">
            {task === null ? 'タスクを追加' : 'タスクを編集'}
          </h2>
          <button
            type="button"
            aria-label="閉じる"
            onClick={onClose}
            className="-mt-1 shrink-0 px-1 text-lg leading-none text-[var(--color-text-muted)]"
          >
            ×
          </button>
        </div>

        {/* 入力候補。同じ表記が増えないよう、既にある値から選べるようにする */}
        <datalist id="fmt-businesses">
          {suggestions.businesses.map((value) => (
            <option key={value} value={value} />
          ))}
        </datalist>
        <datalist id="fmt-categories">
          {suggestions.categories.map((value) => (
            <option key={value} value={value} />
          ))}
        </datalist>
        <datalist id="fmt-middles">
          {suggestions.middleCategories.map((value) => (
            <option key={value} value={value} />
          ))}
        </datalist>
        <datalist id="fmt-dues">
          {suggestions.dueRules.map((value) => (
            <option key={value} value={value} />
          ))}
        </datalist>
        <datalist id="fmt-assignees">
          {suggestions.assignees.map((value) => (
            <option key={value} value={value} />
          ))}
        </datalist>

        <div className="grid gap-3">
          {textField('タスク名', draft.title, undefined, (next) => {
            update({ title: next });
          })}

          <div className="grid grid-cols-2 gap-3">
            {textField('大分類', draft.majorCategory, 'fmt-categories', (next) => {
              update({ majorCategory: next });
            })}
            {textField('中分類', draft.middleCategory, 'fmt-middles', (next) => {
              update({ middleCategory: next });
            })}
          </div>

          <div className="grid grid-cols-3 gap-3">
            {textField('事業', draft.business, 'fmt-businesses', (next) => {
              update({ business: next });
            })}
            {textField('予定', draft.dueRule, 'fmt-dues', (next) => {
              update({ dueRule: next });
            })}
            <label className="block">
              <span className={LABEL_CLASS}>想定時間</span>
              <input
                type="number"
                min={0}
                step={0.25}
                value={hours}
                onChange={(event) => {
                  setHours(event.target.value);
                }}
                className={FIELD_CLASS}
              />
            </label>
          </div>

          {textField('担当', draft.assignee, 'fmt-assignees', (next) => {
            update({ assignee: next });
          })}

          <div className="block">
            <div className="mb-1 flex items-end justify-between gap-3">
              <span className={`${LABEL_CLASS} mb-0`}>
                マニュアル（HTML）
                <span className="ml-2 text-[var(--color-text-muted)]">
                  タスクを開いたときに読める手順書。見出し・箇条書き・表・リンクが使えます
                </span>
              </span>
              {/* 書いた HTML が実際どう出るかは、書きながらでないと分からない。
                  タスク側と同じ描き方で見せる */}
              <div role="tablist" aria-label="マニュアルの表示" className="flex shrink-0 gap-1">
                {[
                  { label: '書く', preview: false },
                  { label: '見る', preview: true },
                ].map((tab) => (
                  <button
                    key={tab.label}
                    type="button"
                    role="tab"
                    aria-selected={previewingManual === tab.preview}
                    onClick={() => {
                      setPreviewingManual(tab.preview);
                    }}
                    className={
                      previewingManual === tab.preview
                        ? 'cursor-pointer rounded bg-[var(--color-bg-subtle)] px-2 py-0.5 text-xs font-medium text-[var(--color-text-primary)]'
                        : 'cursor-pointer rounded px-2 py-0.5 text-xs text-[var(--color-text-muted)]'
                    }
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            {previewingManual ? (
              <div className="min-h-[11rem] rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-3 py-2">
                {hasManual(draft.manual) ? (
                  <ManualView manual={draft.manual ?? ''} />
                ) : (
                  <p className="m-0 py-6 text-center text-sm text-[var(--color-text-muted)]">
                    まだ何も書かれていません
                  </p>
                )}
              </div>
            ) : (
              <textarea
                aria-label="マニュアル（HTML）"
                value={draft.manual ?? ''}
                rows={8}
                placeholder={'<h3>手順</h3>\n<ol>\n  <li>freee を開く</li>\n</ol>'}
                onChange={(event) => {
                  update({ manual: event.target.value });
                }}
                className={`${FIELD_CLASS} resize-y font-mono`}
              />
            )}
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          {onDelete === undefined ? (
            <span />
          ) : confirmingDelete ? (
            <span className="flex items-center gap-2 text-sm">
              <span className="text-[var(--color-text-secondary)]">削除しますか？</span>
              <button
                type="button"
                onClick={() => {
                  onDelete(draft.id);
                }}
                className="rounded border border-[var(--color-overdue)] px-3 py-1.5 text-sm text-[var(--color-overdue)]"
              >
                削除する
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmingDelete(false);
                }}
                className="rounded border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm text-[var(--color-text-secondary)]"
              >
                取りやめる
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => {
                setConfirmingDelete(true);
              }}
              className="rounded border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm text-[var(--color-overdue)]"
            >
              削除
            </button>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-3 py-1.5 text-sm text-[var(--color-text-secondary)]"
            >
              取りやめる
            </button>
            <button
              type="button"
              disabled={!canSave}
              onClick={() => {
                onSave({
                  ...draft,
                  title: draft.title.trim(),
                  majorCategory: draft.majorCategory.trim(),
                  estimatedHours: hours.trim() === '' ? null : Number(hours),
                  // 空のマニュアルは持たせない。保存先に空文字が並ぶだけになる
                  manual: (draft.manual ?? '').trim() === '' ? undefined : draft.manual,
                });
              }}
              className={
                canSave
                  ? 'rounded bg-[var(--color-accent)] px-3 py-1.5 text-sm font-medium text-[var(--color-bg-surface)]'
                  : 'rounded bg-[var(--color-bg-subtle)] px-3 py-1.5 text-sm text-[var(--color-text-muted)]'
              }
            >
              保存
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
